import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from 'fflate';
import { CanvasMetaSchema, LinkRecordSchema, ObjectRecordSchema, typeOwner, type Id, type LinkRecord, type ObjectRecord } from '@vp/shared-types';
import { z } from 'zod';
import type { CanvasMetaExt, StorageAdapter } from './storage/adapter';
import { attachmentRefs, sha256Hex } from './storage/dexie-adapter';
import { checkFormat, DATA_FORMAT_VERSION, FormatTooNewError } from './version';
import { ulid } from './ulid';
import { canvasParts, readCanvasParts, writeCanvasParts } from './canvas-parts';

/**
 * Canvas archive (§13.2, §13.3): zip with a manifest, canvas data, attachments,
 * format versions, checksums and module compatibility info.
 */
export class ArchiveError extends Error {
  readonly code = 'E_ARCHIVE';
  constructor(message: string) {
    super(message);
  }
}

const ManifestSchema = z.object({
  format: z.literal('vp-archive'),
  formatVersion: z.string(),
  app: z.string().optional(),
  createdAt: z.number(),
  canvases: z.array(
    z
      .object({
        id: z.string(),
        name: z.string(),
        file: z.string(),
        objectCount: z.number().int().min(0),
        modules: z.array(z.string()),
        /** Before canvas parts: the file of the modules' data (still written, for older apps). */
        moduleData: z.string().optional(),
        /** Parts of the canvas (canvas-parts.ts): part id → its files. */
        parts: z.record(z.array(z.string())).optional(),
      })
      .passthrough(),
  ),
  attachments: z.array(z.object({ hash: z.string().regex(/^[0-9a-f]{64}$/), mime: z.string(), size: z.number(), file: z.string() })),
  checksums: z.record(z.string()),
  compatibility: z.object({ modules: z.record(z.object({ version: z.string().optional(), types: z.array(z.string()) })) }).passthrough(),
}).passthrough();
export type ArchiveManifest = z.infer<typeof ManifestSchema>;

const CanvasFileSchema = z.object({
  meta: CanvasMetaSchema,
  objects: z.array(ObjectRecordSchema),
  links: z.array(LinkRecordSchema),
});

export interface ParsedCanvas {
  meta: CanvasMetaExt;
  objects: ObjectRecord[];
  links: LinkRecord[];
  /** Parts of the canvas by part id (canvas-parts.ts): the modules' data and what comes later. */
  parts: Record<string, unknown>;
}

export interface ParsedArchive {
  manifest: ArchiveManifest;
  canvases: ParsedCanvas[];
  attachments: { hash: string; mime: string; data: Uint8Array }[];
  /** Module ids referenced by objects. */
  modules: string[];
}

export async function buildArchive(
  storage: StorageAdapter,
  canvasIds: Id[],
  info: {
    app?: string;
    moduleVersions?: Record<string, string>;
    /** Current in-memory content of a canvas (when saving to storage is failing, the stored copy is behind). */
    live?: (id: Id) => { objects: ObjectRecord[]; links: LinkRecord[] } | undefined;
  } = {},
): Promise<Uint8Array> {
  const files: Zippable = {};
  const checksums: Record<string, string> = {};
  const manifest: ArchiveManifest = {
    format: 'vp-archive',
    formatVersion: DATA_FORMAT_VERSION,
    app: info.app,
    createdAt: Date.now(),
    canvases: [],
    attachments: [],
    checksums,
    compatibility: { modules: {} },
  };
  const attachmentHashes = new Set<string>();
  for (const id of canvasIds) {
    const meta = await storage.getCanvas(id);
    if (!meta) continue;
    const { objects, links } = info.live?.(id) ?? (await storage.readCanvasContent(id));
    const modules = new Set<string>();
    for (const o of objects) {
      const owner = typeOwner(o.type);
      if (owner) {
        modules.add(owner);
        const entry = (manifest.compatibility.modules[owner] ??= { version: info.moduleVersions?.[owner], types: [] });
        if (!entry.types.includes(o.type)) entry.types.push(o.type);
      }
      for (const h of attachmentRefs(o)) attachmentHashes.add(h);
    }
    for (const l of links) {
      const owner = typeOwner(l.type);
      if (owner) modules.add(owner);
    }
    const file = `canvases/${id}.json`;
    const data = strToU8(JSON.stringify({ meta: { ...meta, appliedSeq: undefined }, objects, links }));
    files[file] = data;
    checksums[file] = await sha256Hex(data);
    const partFiles: Record<string, string[]> = {};
    const values = await readCanvasParts(storage, id);
    for (const part of canvasParts()) {
      if (!(part.id in values)) continue;
      const out = await part.toFiles(values[part.id]);
      partFiles[part.id] = [];
      for (const [name, bytes] of Object.entries(out)) {
        const path = `parts/${part.id}/${id}/${name}`;
        files[path] = bytes;
        checksums[path] = await sha256Hex(bytes);
        partFiles[part.id].push(path);
      }
    }
    // older apps read the modules' data from this field: it points at the same file
    const legacy = partFiles['moduleData']?.find((f) => f.endsWith('/data.json'));
    manifest.canvases.push({ id, name: meta.name, file, objectCount: objects.length, modules: [...modules], ...(legacy ? { moduleData: legacy } : {}), ...(Object.keys(partFiles).length ? { parts: partFiles } : {}) });
  }
  for (const hash of attachmentHashes) {
    const blob = await storage.getAttachment(hash);
    if (!blob) continue;
    const data = new Uint8Array(await blob.arrayBuffer());
    const file = `attachments/${hash}`;
    files[file] = [data, { level: 0 }];
    checksums[file] = hash;
    manifest.attachments.push({ hash, mime: blob.type || 'application/octet-stream', size: data.byteLength, file });
  }
  files['manifest.json'] = strToU8(JSON.stringify(manifest, null, 2));
  return zipSync(files, { level: 6 });
}

/** Validate structure, checksums, versions and schemas before anything is written (§13.3). */
export async function readArchive(data: Uint8Array): Promise<ParsedArchive> {
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(data);
  } catch {
    throw new ArchiveError('not a zip archive');
  }
  const mf = entries['manifest.json'];
  if (!mf) throw new ArchiveError('manifest.json is missing');
  let raw: unknown;
  try {
    raw = JSON.parse(strFromU8(mf));
  } catch {
    throw new ArchiveError('manifest.json is not valid JSON');
  }
  const fv = (raw as { formatVersion?: unknown })?.formatVersion;
  if (typeof fv === 'string' && checkFormat(fv) === 'too-new') throw new FormatTooNewError(fv);
  const parsed = ManifestSchema.safeParse(raw);
  if (!parsed.success) throw new ArchiveError('invalid manifest: ' + parsed.error.issues[0]?.path.join('.') + ' ' + parsed.error.issues[0]?.message);
  const manifest = parsed.data;
  if (checkFormat(manifest.formatVersion) === 'invalid') throw new ArchiveError('invalid format version');

  for (const [file, sum] of Object.entries(manifest.checksums)) {
    const d = entries[file];
    if (!d) throw new ArchiveError(`missing file ${file}`);
    if ((await sha256Hex(d)) !== sum) throw new ArchiveError(`checksum mismatch: ${file}`);
  }
  const canvases: ParsedCanvas[] = [];
  const modules = new Set<string>();
  for (const c of manifest.canvases) {
    const d = entries[c.file];
    if (!d || !manifest.checksums[c.file]) throw new ArchiveError(`missing canvas file ${c.file}`);
    let json: unknown;
    try {
      json = JSON.parse(strFromU8(d));
    } catch {
      throw new ArchiveError(`invalid JSON in ${c.file}`);
    }
    const cf = CanvasFileSchema.safeParse(json);
    if (!cf.success) throw new ArchiveError(`invalid canvas data in ${c.file}: ${cf.error.issues[0]?.path.join('.')} ${cf.error.issues[0]?.message}`);
    const ids = new Set<string>();
    for (const o of cf.data.objects) {
      if (ids.has(o.id)) throw new ArchiveError(`duplicate object id in ${c.file}`);
      ids.add(o.id);
      const owner = typeOwner(o.type);
      if (owner) modules.add(owner);
    }
    const listed: Record<string, string[]> = { ...(c.parts ?? {}) };
    // an archive from before canvas parts: only the modules' data, in its own field
    if (!listed['moduleData'] && c.moduleData) listed['moduleData'] = [c.moduleData];
    const parts: Record<string, unknown> = {};
    for (const part of canvasParts()) {
      const paths = listed[part.id];
      if (!paths) continue;
      const own: Record<string, Uint8Array> = {};
      for (const path of paths) {
        const d = entries[path];
        if (!d || !manifest.checksums[path]) throw new ArchiveError(`missing ${part.id} file ${path}`);
        // the legacy file is named after the canvas; the part reads it as its data.json
        own[path === c.moduleData && !c.parts?.['moduleData'] ? 'data.json' : path.slice(path.lastIndexOf('/') + 1)] = d;
      }
      try {
        parts[part.id] = await part.fromFiles(own);
      } catch (e) {
        throw new ArchiveError(`invalid ${part.id} in ${c.file}: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    canvases.push({ meta: cf.data.meta as CanvasMetaExt, objects: cf.data.objects as ObjectRecord[], links: cf.data.links as LinkRecord[], parts });
  }
  const attachments = manifest.attachments.map((a) => {
    const d = entries[a.file];
    if (!d) throw new ArchiveError(`missing attachment ${a.file}`);
    return { hash: a.hash, mime: a.mime, data: d };
  });
  for (const a of attachments) {
    if ((await sha256Hex(a.data)) !== a.hash) throw new ArchiveError(`attachment hash mismatch ${a.hash}`);
  }
  return { manifest, canvases, attachments, modules: [...modules] };
}

/**
 * Apply a validated archive: every canvas becomes a new canvas (fresh id), written
 * atomically; on failure, canvases created by this import are removed and existing
 * canvases are never touched. Unknown types and fields are kept verbatim.
 */
export async function applyArchive(storage: StorageAdapter, archive: ParsedArchive): Promise<Id[]> {
  for (const a of archive.attachments) {
    await storage.putAttachment(new Blob([a.data as BlobPart], { type: a.mime }), a.mime);
  }
  const created: Id[] = [];
  try {
    for (const c of archive.canvases) {
      const id = ulid();
      const now = Date.now();
      const meta: CanvasMetaExt = {
        ...c.meta,
        id,
        createdAt: c.meta.createdAt ?? now,
        updatedAt: now,
        formatVersion: DATA_FORMAT_VERSION,
        lastExportAt: null,
        objectCount: c.objects.length,
      };
      delete meta.appliedSeq;
      await storage.createCanvasWithContent(
        meta,
        c.objects.map((o) => ({ ...o, canvasId: id })),
        c.links.map((l) => ({ ...l, canvasId: id })),
      );
      created.push(id);
      await writeCanvasParts(storage, id, c.parts);
    }
  } catch (e) {
    for (const id of created) await storage.deleteCanvas(id).catch(() => undefined);
    throw e;
  }
  return created;
}
